import { RefObject, useLayoutEffect, useRef, useState } from "react";

import { DashboardWindow } from "./dashboardHelpers";

const VIEWPORT_MARGIN = 8;
const HEADER_HEIGHT = 64; // matches the h-16 sticky navbar
// Gap between the trigger (action-buttons container) and the popover, per the Figma edit
// popover: it opens just below the buttons rather than covering them.
const TRIGGER_GAP = 8;

interface PopoverPosition {
  style: React.CSSProperties;
}

/**
 * Dynamically positions a popover relative to its parent container,
 * flipping horizontally/vertically to stay within the viewport.
 *
 * Per the Figma edit popover, the preferred placement is below the trigger (with a small
 * gap so the trigger buttons stay visible) and left-aligned to it, extending rightward over
 * the block's right neighbor so the edited block stays visible. The last block in a row
 * slides left just enough to fit the viewport, sitting at the screen's right edge; vertical
 * overflow flips the popover above the trigger. Viewport clamping is the last resort.
 *
 * The popover element must use `position: absolute` and its nearest
 * positioned ancestor is the anchor (typically the action-buttons container).
 */
export function usePopoverPosition(
  popoverRef: RefObject<HTMLDivElement | null>,
  isOpen: boolean,
  targetWindow: DashboardWindow,
  popoverWidth: number
): PopoverPosition {
  // Width from the first frame, so text wraps (and the height measured below) match the
  // positioned popover.
  const [style, setStyle] = useState<React.CSSProperties>({
    position: "absolute",
    visibility: "hidden",
    width: popoverWidth,
  });
  // Which side of the trigger this open settled on; reset on close so the next open decides
  // afresh from wherever the block is then.
  const verticalSideRef = useRef<"below" | "above" | "clamped" | null>(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      verticalSideRef.current = null;
      return;
    }

    const calculate = () => {
      const popoverEl = popoverRef.current;
      if (!popoverEl) {
        return;
      }

      const parentEl = popoverEl.offsetParent as HTMLElement | null;
      if (!parentEl) {
        return;
      }

      const parentRect = parentEl.getBoundingClientRect();
      const popoverHeight = popoverEl.scrollHeight || popoverEl.offsetHeight;
      const vw = targetWindow.innerWidth;
      const vh = targetWindow.innerHeight;

      // The chat sidebar OVERLAYS the iframe's right side: the iframe stays full-width and
      // a flex spacer reserves the covered region instead, so `innerWidth` includes pixels
      // the sidebar hides. The spacer's left edge is exactly where visible content ends —
      // the viewport edge while the sidebar is closed (spacer width 0), the sidebar's edge
      // while open — so clamp to it rather than to <main>, whose own right padding would
      // needlessly waste the page gutter when the sidebar is closed.
      const spacerEl = targetWindow.document.querySelector("[data-chat-sidebar-spacer]");
      const rightBound = Math.min(vw, spacerEl?.getBoundingClientRect().left ?? vw);

      const newStyle: React.CSSProperties = {
        position: "absolute",
        width: popoverWidth,
        visibility: "visible",
      };

      // Horizontal: left-aligned to the trigger, extending rightward over the block's right
      // neighbor (per the Figma, leaving the edited block visible). When that would
      // overflow — the last block in a row, or the chat sidebar eating into the content —
      // slide left just enough to fit, so the popover sits at the visible area's right edge
      // below the trigger. The edited block stays identified by its highlight ring (see
      // DraggableBlock) wherever the popover lands.
      const clampedLeft = Math.max(
        VIEWPORT_MARGIN,
        Math.min(rightBound - popoverWidth - VIEWPORT_MARGIN, parentRect.left)
      );
      // Convert viewport-relative to parent-relative
      newStyle.left = clampedLeft - parentRect.left;

      const topBound = HEADER_HEIGHT + VIEWPORT_MARGIN;

      // Vertical: below the trigger with a gap so the buttons stay visible; flip fully
      // above the trigger when the bottom overflows; clamp to the viewport (staying below
      // the sticky header) as a last resort.
      //
      // The side is decided ONCE per open and then held. The popover's height changes while
      // it is open (instant-apply controls add and remove rows), and re-deciding the side on
      // every change made it jump: a popover anchored above the trigger grows upward, and a
      // popover below flips above the moment a new row pushes its bottom past the viewport.
      // Holding the side turns growth into a clamp instead — the popover stays where the
      // user opened it and only slides as far as it must to stay on screen.
      const fitsBelow =
        parentRect.bottom + TRIGGER_GAP >= topBound &&
        parentRect.bottom + TRIGGER_GAP + popoverHeight + VIEWPORT_MARGIN <= vh;
      const fitsAbove = parentRect.top - TRIGGER_GAP - popoverHeight >= topBound;
      if (verticalSideRef.current === null) {
        verticalSideRef.current = fitsBelow ? "below" : fitsAbove ? "above" : "clamped";
      }

      const clampTop = () => {
        const clampedTop = Math.max(
          topBound,
          Math.min(vh - popoverHeight - VIEWPORT_MARGIN, parentRect.top)
        );
        newStyle.top = clampedTop - parentRect.top;
      };

      if (verticalSideRef.current === "below") {
        if (fitsBelow) {
          newStyle.top = parentRect.height + TRIGGER_GAP;
        } else {
          // Grew past the bottom: slide up just enough, from the top edge the user knows.
          const belowTop = parentRect.bottom + TRIGGER_GAP;
          const clampedTop = Math.max(
            topBound,
            Math.min(vh - popoverHeight - VIEWPORT_MARGIN, belowTop)
          );
          newStyle.top = clampedTop - parentRect.top;
        }
      } else if (verticalSideRef.current === "above") {
        if (fitsAbove) {
          newStyle.bottom = parentRect.height + TRIGGER_GAP;
        } else {
          clampTop();
        }
      } else {
        clampTop();
      }

      setStyle(newStyle);
    };

    // Run initial calculation after a frame so the popover is mounted and measurable
    const rafId = targetWindow.requestAnimationFrame(calculate);

    // No scroll listener on purpose. The popover is absolutely positioned inside the block's
    // anchor, so it scrolls with the block by itself. Recalculating on scroll only served to
    // re-clamp it to the viewport, which pinned the popover to the screen edge while its
    // block scrolled out of view — it should leave the frame with the block instead.
    targetWindow.addEventListener("resize", calculate);

    // Opening/closing/resizing the chat sidebar animates the spacer's width WITHOUT
    // resizing the iframe (the sidebar overlays it), so no `resize` event fires — observe
    // the spacer directly to keep an open popover clear of the sidebar.
    const spacerEl = targetWindow.document.querySelector("[data-chat-sidebar-spacer]");
    const ResizeObserverCtor = (targetWindow as unknown as Window & typeof globalThis)
      .ResizeObserver;
    const resizeObserver =
      spacerEl && ResizeObserverCtor ? new ResizeObserverCtor(calculate) : undefined;
    resizeObserver?.observe(spacerEl!);

    // The popover's own height is content-driven and changes while it is open: instant-apply
    // controls add and remove rows (switching a chart to bar reveals stacking + both axis
    // rows, ~96px). `calculate` measures height once per run, so without this the committed
    // top/bottom would stay put and the extra rows — Done included — would grow off-screen.
    // A max-height with overflow is NOT an alternative here: it would clip the icon picker
    // and select menus, which are absolutely positioned and must escape the panel.
    const selfObserver = ResizeObserverCtor ? new ResizeObserverCtor(calculate) : undefined;
    if (popoverRef.current) {
      selfObserver?.observe(popoverRef.current);
    }

    return () => {
      targetWindow.cancelAnimationFrame(rafId);
      targetWindow.removeEventListener("resize", calculate);
      resizeObserver?.disconnect();
      selfObserver?.disconnect();
    };
  }, [isOpen, popoverRef, popoverWidth, targetWindow]);

  return { style };
}
